import React, { useState, useEffect } from "react";
import axios from "axios";
import RadioButton from "./RadioButton";
import DateTimePicker from "./DateTimePicker";
import Operators from "./Operators";
import QcList from "./QcList";
import { format } from "date-fns";
import { toast } from "react-toastify";

const rdOnly =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed";
const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

function UpdateModal({ isVisible, onClose, orderData, refreshData }) {
  const [radioSelected, setRadioSelected] = useState("");
  const [inputDateSelected, setInputDateSelected] = useState(new Date());
  const [deliveryDateSelected, setDeliveryDateSelected] = useState(
    new Date(new Date().setDate(new Date().getDate() + 7)),
  );
  const [qtyOrder, setQtyOrder] = useState("");
  const [poOrder, setPoOrder] = useState("");
  const [oprSelected, setOprSelected] = useState("");
  const [qcSelected, setQcSelected] = useState("");

  useEffect(() => {
    if (orderData) {
      setRadioSelected(orderData.status_id);
      setInputDateSelected(new Date(orderData.date));
      setDeliveryDateSelected(new Date(orderData.dlvr_date));
      setQtyOrder(orderData.qty);
      setPoOrder(orderData.po_num);
      setOprSelected(orderData.opr_id);
      setQcSelected(orderData.qc_id);
    }
  }, [orderData]);

  if (!isVisible) return null;

  const updateOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.patch("https://eps.elkomprintama.com/patch/api/orders/", {
        dateOrder: format(inputDateSelected, "yyyy-MM-dd"),
        delivery: format(deliveryDateSelected, "yyyy-MM-dd"),
        operator: oprSelected,
        qc: qcSelected,
        status_id: radioSelected,
        order_id: orderData.order_id,
        qty: qtyOrder,
        po_number: poOrder,
      });
      toast.success("Order updated.");
      onClose();
      refreshData();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Update failed.");
    }
  };

  const handleClose = () => {
    setRadioSelected(orderData.status_id);
    setInputDateSelected(new Date(orderData.date));
    setDeliveryDateSelected(new Date(orderData.dlvr_date));
    setQtyOrder(orderData.qty);
    setPoOrder(orderData.po_num);
    setOprSelected(orderData.opr_id);
    setQcSelected(orderData.qc_id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">Update Order</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Order ID: {orderData.order_id}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={updateOrder}
          className="overflow-y-auto flex-1 px-6 py-5 space-y-4"
        >
          {/* Status */}
          <div>
            <label className={lbl}>Status</label>
            <RadioButton
              radioData={radioSelected}
              onchangeDate={(e) => setRadioSelected(e.target.value)}
            />
          </div>

          {/* Read-only info */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className={lbl}>Order ID</label>
              <input
                className={rdOnly}
                value={orderData.order_id}
                readOnly
                disabled
              />
            </div>
            <div className="col-span-3">
              <label className={lbl}>Customer</label>
              <input
                className={rdOnly}
                value={orderData.nme}
                readOnly
                disabled
              />
            </div>
          </div>

          <div>
            <label className={lbl}>Jobsheet</label>
            <input
              className={rdOnly}
              value={orderData.job_name}
              readOnly
              disabled
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Size</label>
              <input
                className={rdOnly}
                value={orderData.size}
                readOnly
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Cust. P/N</label>
              <input
                className={rdOnly}
                value={orderData.pn_cust || "—"}
                readOnly
                disabled
              />
            </div>
          </div>

          {/* Dates */}
          <DateTimePicker
            firstDateValue={inputDateSelected}
            onChangeFirstDate={setInputDateSelected}
            secondDateValue={deliveryDateSelected}
            onChangeSecondDate={setDeliveryDateSelected}
            firstLabel="Order Date"
            secondLabel="Delivery Date"
          />

          {/* Qty + PO */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Order Qty</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                type="text"
                value={qtyOrder}
                onChange={(e) => setQtyOrder(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>PO Number</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                type="text"
                value={poOrder}
                onChange={(e) => setPoOrder(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          {/* Operator + QC */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Operator</label>
              <Operators
                onChangeOpr={(e) => setOprSelected(e.target.value)}
                oprData={oprSelected}
              />
            </div>
            <div>
              <label className={lbl}>QC</label>
              <QcList
                onChangeQc={(e) => setQcSelected(e.target.value)}
                qcData={qcSelected}
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition"
            >
              Update Order
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateModal;
